import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, CardMedia, Button, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { MenuBook } from '@mui/icons-material';

type Props = {};

const AuthorIndex = (props: Props) => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography 
          variant="h1" 
          sx={{ 
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            fontWeight: 700,
            color: '#2c3e50',
            mb: 2,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}
        >
          Bibliography
        </Typography>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontSize: { xs: '1.1rem', md: '1.25rem' },
            color: '#7f8c8d',
            fontStyle: 'italic',
            fontFamily: 'Georgia, serif',
            maxWidth: '700px',
            mx: 'auto'
          }}
        >
          Explore the literary works of Philip Payne - stories born from experience, 
          crafted with heart, and told with authenticity
        </Typography>
      </Box>

      {/* Books Grid */}
      <Grid container spacing={4}>
        {/* RON Book Card */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
              }
            }}
            onClick={() => navigate('/bibliography/ron')}
          >
            <CardMedia
              component="img"
              height="400"
              image="/phil-holding-ron.jpeg"
              alt="RON by Philip Payne"
              sx={{
                objectFit: 'cover',
                objectPosition: 'center'
              }}
            />
            <CardContent sx={{ flexGrow: 1, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <MenuBook sx={{ color: '#e74c3c', fontSize: 32 }} />
                <Typography 
                  variant="h4" 
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    color: '#2c3e50',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                  }}
                >
                  RON
                </Typography>
              </Box>
              
              <Chip 
                label="ISBN: 979-8455389184" 
                size="small"
                sx={{ 
                  mb: 2,
                  backgroundColor: '#ecf0f1',
                  color: '#2c3e50',
                  fontFamily: 'monospace'
                }} 
              />

              <Typography 
                sx={{ 
                  color: '#555',
                  lineHeight: 1.8,
                  mb: 3,
                  fontFamily: 'Georgia, serif'
                }}
              >
                After trying to break up a bar fight, Ron finds himself wrongly accused and imprisoned. 
                Released but struggling, he's recruited for questionable work by a government department, 
                walking a thin line between right and wrong...
              </Typography>

              <Button
                variant="contained"
                fullWidth
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/bibliography/ron');
                }}
                sx={{
                  backgroundColor: '#e74c3c',
                  '&:hover': {
                    backgroundColor: '#c0392b'
                  },
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5
                }}
              >
                View Book Details
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* A Nation's Cry Book Card */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
              }
            }}
            onClick={() => navigate('/bibliography/a-nations-cry')}
          >
            <CardMedia
              component="img"
              height="400"
              image="/A_Nations_cry.jpg"
              alt="A Nation's Cry by Philip Payne"
              sx={{
                objectFit: 'cover',
                objectPosition: 'center'
              }}
            />
            <CardContent sx={{ flexGrow: 1, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <MenuBook sx={{ color: '#3498db', fontSize: 32 }} />
                <Typography 
                  variant="h4" 
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    color: '#2c3e50',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                  }}
                >
                  A Nation's Cry
                </Typography>
              </Box>
              
              <Chip 
                label="ISBN: 979-8323680931" 
                size="small"
                sx={{ 
                  mb: 2,
                  backgroundColor: '#ecf0f1',
                  color: '#2c3e50',
                  fontFamily: 'monospace'
                }} 
              />

              <Typography 
                sx={{ 
                  color: '#555',
                  lineHeight: 1.8,
                  mb: 3,
                  fontFamily: 'Georgia, serif'
                }}
              >
                After centuries of peace, Tagonia suspects trouble brewing from its neighbour. 
                With an ambitious emperor's son building an army for conquest and a new queen 
                unprepared for war, the nation faces its greatest challenge...
              </Typography>

              <Button
                variant="contained"
                fullWidth
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/bibliography/a-nations-cry');
                }}
                sx={{
                  backgroundColor: '#3498db',
                  '&:hover': {
                    backgroundColor: '#2980b9'
                  },
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5
                }}
              >
                View Book Details
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Redrock Outhouse Book Card */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
              }
            }}
            onClick={() => navigate('/bibliography/redrock-outhouse')}
          >
            <CardMedia
              component="img"
              height="400"
              image="/Redrock_Outhouse.jpg"
              alt="Redrock Outhouse by Philip Payne"
              sx={{
                objectFit: 'cover',
                objectPosition: 'center'
              }}
            />
            <CardContent sx={{ flexGrow: 1, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <MenuBook sx={{ color: '#16a085', fontSize: 32 }} />
                <Typography 
                  variant="h4" 
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    color: '#2c3e50',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                  }}
                >
                  Redrock Outhouse
                </Typography>
              </Box>
              
              <Chip 
                label="ASIN: B0FWM8PV8Q" 
                size="small"
                sx={{ 
                  mb: 2,
                  backgroundColor: '#ecf0f1',
                  color: '#2c3e50',
                  fontFamily: 'monospace'
                }} 
              />

              <Typography 
                sx={{ 
                  color: '#555',
                  lineHeight: 1.8,
                  mb: 3,
                  fontFamily: 'Georgia, serif'
                }}
              >
                When an honest carpenter sets out to make an honest living, the privileged sons 
                of the richest man in the county make things difficult. No longer prepared to 
                look the other way, Robert seeks justice...
              </Typography>

              <Button
                variant="contained"
                fullWidth
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/bibliography/redrock-outhouse');
                }}
                sx={{
                  backgroundColor: '#16a085',
                  '&:hover': {
                    backgroundColor: '#138d75'
                  },
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5
                }}
              >
                View Book Details
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AuthorIndex;
